import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useThemeStore } from '../../stores/theme.store';
import { authApi } from '../../api/auth.api';
import { MapPin, LogOut, Sun, Moon, BookOpen, Globe, Compass, Settings, ShieldCheck, Building2, Menu, X, Icon } from '../ui/Icon';
import { useMyAgencies } from '../../stores/agencies.store';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../../i18n';
import TwoFactorModal from '../auth/TwoFactorModal';
import UnverifiedBanner from '../auth/UnverifiedBanner';
import InstallPrompt from '../ui/InstallPrompt';
import { UserAvatar } from '../ui/UserAvatar';
import { GromlyWordmark } from '../brand/GromlyMark';
import ModeSwitch from './ModeSwitch';
import ModeSidebar from './ModeSidebar';
import ActingAgencyBanner from './ActingAgencyBanner';
import { ADMIN_NAV, agencyNavItems, type ModeNav, type NavItem } from './modeNav';

const PERSONAL_NAV: NavItem[] = [
  { to: '/dashboard', icon: MapPin, labelKey: 'nav.myTrips' },
  { to: '/explore', icon: Compass, labelKey: 'nav.explore' },
  { to: '/docs', icon: BookOpen, labelKey: 'nav.docs' },
];

export default function AppShell() {
  const { user, clearAuth } = useAuthStore();
  const { isDark, toggle } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
      if (mobileNavRef.current && !mobileNavRef.current.contains(e.target as Node)) setMobileNavOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close the mobile nav whenever the route changes
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // ADR-0036: an account switches between the experiences it has (its own
  // trips, its agency's, Gromly's administration) and never sees the others.
  const mine = useMyAgencies();
  const agency = mine.status === 'ready' ? mine.agency : null;
  const inAdmin = mine.status === 'ready' && mine.mode === 'admin';
  const modeNav: ModeNav | null = agency ? agencyNavItems(agency.id) : inAdmin ? ADMIN_NAV : null;
  const navItems: NavItem[] = mine.status === 'loading' ? [] : modeNav ? [...modeNav.main, ...modeNav.footer] : PERSONAL_NAV;
  // Inside a trip its own sidebar navigates; two side columns would eat the map.
  const showModeSidebar = modeNav !== null && !location.pathname.startsWith('/trips/');
  const onAccountScreen = location.pathname === '/account';

  async function handleLogout() {
    await authApi.logout().catch(() => null);
    clearAuth();
    navigate('/login');
  }

  const currentLang = LANGUAGES.find(l => l.code === i18n.language?.slice(0, 2)) ?? LANGUAGES[0];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950 overflow-hidden">
      {/* Global topbar */}
      <header className="min-h-[calc(3.5rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-white dark:bg-slate-900 border-b border-slate-200/70 dark:border-slate-800 flex items-center px-3 sm:px-5 gap-2 sm:gap-4 shrink-0 z-20 shadow-sm">
        {/* Mobile menu toggle */}
        <div className="relative md:hidden" ref={mobileNavRef}>
          <button
            onClick={() => setMobileNavOpen(o => !o)}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            aria-label={t('nav.menu')}
            aria-expanded={mobileNavOpen}
          >
            <Icon as={mobileNavOpen ? X : Menu} size={20} />
          </button>
          {mobileNavOpen && (
            <div className="absolute left-0 top-full mt-1 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-50 py-1">
              {navItems.map(({ to, icon, labelKey }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`
                  }
                >
                  <Icon as={icon} size={16} />
                  {t(labelKey)}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        <Link to="/dashboard" className="flex items-center group hover:opacity-90 transition-opacity shrink-0">
          <GromlyWordmark size={26} dark={isDark} />
        </Link>

        {/* In agency and admin mode the sidebar holds the navigation. */}
        {!modeNav && <nav className="hidden md:flex items-center gap-1 ml-2">
          {navItems.map(({ to, icon, labelKey }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`
              }
            >
              <Icon as={icon} size={14} />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>}

        <div className="flex-1" />

        {/* Right controls */}
        <div className="flex items-center gap-1">

          {/* Language picker */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(o => !o)}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer text-xs font-semibold"
              aria-label={t('nav.language')}
            >
              <Icon as={Globe} size={14} />
              {currentLang.label}
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-50">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => { i18n.changeLanguage(lang.code); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors cursor-pointer text-left ${
                      i18n.language?.startsWith(lang.code)
                        ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-medium'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="w-6 text-xs font-bold text-slate-400">{lang.label}</span>
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dark mode toggle */}
          <button
            onClick={toggle}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
            aria-label={isDark ? t('nav.lightMode') : t('nav.darkMode')}
          >
            {isDark
              ? <Icon as={Sun} size={16} />
              : <Icon as={Moon} size={16} />
            }
          </button>

          {/* User menu */}
          <div className="relative" ref={userRef}>
            <button
              onClick={() => setUserOpen(o => !o)}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors cursor-pointer ${onAccountScreen ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              <UserAvatar key={user?.avatarUrl ?? 'none'} name={user?.displayName || user?.username || ''} avatarUrl={user?.avatarUrl} size={28} />
              <span className={`text-sm font-medium hidden sm:block ${onAccountScreen ? 'text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-300'}`}>
                {user?.displayName || user?.username}
              </span>
            </button>
            {userOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-50">
                <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{user?.displayName || user?.username}</p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">{user?.email}</p>
                </div>
                {mine.status === 'ready' && mine.modes.length > 1 && (
                  <ModeSwitch agency={mine.agencies[0] ?? null} modes={mine.modes} mode={mine.mode}
                    onMode={mine.setMode} onDone={() => setUserOpen(false)} />
                )}
                <button
                  onClick={() => { setUserOpen(false); navigate('/account'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <Icon as={Settings} size={14} />
                  {t('nav.accountSettings')}
                </button>
                <button
                  onClick={() => { setUserOpen(false); setTwoFAOpen(true); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <Icon as={ShieldCheck} size={14} />
                  {t('nav.security')}
                </button>
                <div className="border-t border-slate-100 dark:border-slate-800" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                >
                  <Icon as={LogOut} size={14} />
                  {t('nav.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <UnverifiedBanner />
      <ActingAgencyBanner />

      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {showModeSidebar && modeNav && (agency
          ? <ModeSidebar title={agency.name} logoUrl={agency.logoUrl} icon={Building2} nav={modeNav} />
          : <ModeSidebar title={t('nav.mode.admin')} icon={ShieldCheck} nav={modeNav} />)}
        <main className="flex-1 min-w-0 overflow-auto">
          <Outlet />
        </main>
      </div>

      {twoFAOpen && <TwoFactorModal onClose={() => setTwoFAOpen(false)} />}
      <InstallPrompt />
    </div>
  );
}
